// src/config/urgencyStatuses.ts
//
// Configuration for service urgency status values.
// Used by UpcomingServiceFilters and UrgencyMultiSelector.

import { MantineColor } from "@mantine/core";
import { UrgencyStatus } from "@/utils/api";

/**
 * Urgency status constants matching backend values.
 */
export const URGENCY_STATUSES = {
  OVERDUE: "overdue",
  DUE_SOON: "due_soon",
  UPCOMING: "upcoming",
  OK: "ok",
} as const;

/**
 * Configuration for each urgency status including icon, color, and translation key.
 */
export interface UrgencyStatusConfig {
  value: UrgencyStatus;
  /** Icon name for AppIcon component */
  icon: string;
  color: MantineColor;
  translationKey: string;
}

/**
 * Urgency status configurations mapped by value.
 */
export const URGENCY_STATUS_CONFIG: Record<UrgencyStatus, UrgencyStatusConfig> = {
  [URGENCY_STATUSES.OVERDUE]: {
    value: URGENCY_STATUSES.OVERDUE,
    icon: "AlertTriangle",
    color: "red",
    translationKey: "services:urgencyFilter.overdue",
  },
  [URGENCY_STATUSES.DUE_SOON]: {
    value: URGENCY_STATUSES.DUE_SOON,
    icon: "Clock",
    color: "orange",
    translationKey: "services:urgencyFilter.dueSoon",
  },
  [URGENCY_STATUSES.UPCOMING]: {
    value: URGENCY_STATUSES.UPCOMING,
    icon: "Calendar",
    color: "blue",
    translationKey: "services:urgencyFilter.upcoming",
  },
  [URGENCY_STATUSES.OK]: {
    value: URGENCY_STATUSES.OK,
    icon: "CircleCheck",
    color: "green",
    translationKey: "services:urgencyFilter.ok",
  },
};

/**
 * Get urgency status configuration by value.
 */
export const getUrgencyStatusConfig = (
  value: string | undefined | null
): UrgencyStatusConfig | undefined => {
  if (!value) return undefined;
  return URGENCY_STATUS_CONFIG[value as UrgencyStatus];
};

/**
 * Array of all urgency status configurations for iteration.
 */
export const URGENCY_STATUS_OPTIONS: UrgencyStatusConfig[] =
  Object.values(URGENCY_STATUS_CONFIG);